import React, {
  useEffect,
  useState,
  useMemo,
  useContext,
} from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';
import { compose } from 'recompose';
import format from 'string-template';
import { firestorePaths, pathPlaceholder } from '../../utils/firebasePaths';

import User from '../../Model/User';
import UserConfig from '../../Model/UserConfig';
import UserPresence from '../../Model/UserPresence';
import useSessionStore from '../../hooks/useSessionStore';
import useComponentMounted from '../../hooks/useComponentMounted';

import UserContext from '../UserContext';

import LoggedInUserContext from './LoggedInUserContext';

const LoggedInUserContextProvider = ({
  children,
}) => {
  const {
    userId: userInRouteId,
    userDoc: userInRouteDoc,
    userConfigDoc: userInRouteConfigDoc,
  } = useContext(UserContext);

  const isComponentMountedRef = useComponentMounted();

  const { authUser: { uid: authUserId } = {}, isInsideSalesUser } = useSessionStore();

  const [isReady, setIsReady] = useState(authUserId === userInRouteId);
  const [userDoc, setUserDoc] = useState(() => (isReady ? userInRouteDoc : null));
  const [userConfigDoc, setUserConfigDoc] = useState(() => (isReady ? userInRouteConfigDoc : null));
  const [userPresenceDoc, setUserPresenceDoc] = useState(null);

  useEffect(() => {
    const init = async () => {
      const loggedInUserDoc = new User(format(firestorePaths.USER_DOC, {
        [pathPlaceholder.USER_ID]: authUserId,
      }));

      const loggedInUserConfigDoc = new UserConfig(format(firestorePaths.USER_CONFIG_DOC, {
        [pathPlaceholder.USER_ID]: authUserId,
      }));

      await Promise.all([
        loggedInUserDoc.init(),
        loggedInUserConfigDoc.init(),
      ]);

      if (isComponentMountedRef.current) {
        setUserDoc(loggedInUserDoc);
        setUserConfigDoc(loggedInUserConfigDoc);
        setIsReady(true);
      }
    };

    if (!isReady) {
      init();
    }
  }, [
    isReady,
    authUserId,
    isComponentMountedRef,
  ]);

  // Get the inside sales user presence doc
  useEffect(() => {
    const init = async () => {
      if (isInsideSalesUser) {
        const loggedInUserPresenceDoc = await UserPresence.getUserPresenceByUserId(authUserId);

        if (isComponentMountedRef.current) {
          setUserPresenceDoc(loggedInUserPresenceDoc);
        }
      }
    };

    init();
  }, [
    authUserId,
    isInsideSalesUser,
    isComponentMountedRef,
  ]);

  const context = useMemo(() => ({
    userId: authUserId,
    userDoc,
    userConfigDoc,
    isReady,
    setIsReady,
    userPresenceDoc,
  }), [
    authUserId,
    userDoc,
    userConfigDoc,
    isReady,
    setIsReady,
    userPresenceDoc,
  ]);

  return (
    <LoggedInUserContext.Provider value={context}>
      {children}
    </LoggedInUserContext.Provider>
  );
};

LoggedInUserContextProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default compose(
  observer,
)(LoggedInUserContextProvider);
